<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useMutation, useQuery, useLazyQuery } from '@vue/apollo-composable';
import { graphql } from '@/gql';
import { useNotificationStore } from '@/stores/notification';
import { useRouteParams } from '@vueuse/router';
import { VCard, VCardText } from 'vuetify/components';
import AppModal from './AppModal.vue';
import DeliverableLinkForm, {
    type LinkDeliverableFormValues,
} from './DeliverableLinkForm.vue';
import type { Deliverable, LinkDeliverableInput, Project } from '@/gql/graphql';
import { computed, onMounted, ref, watch } from 'vue';

const { t } = useI18n({
    messages: {
        nl: {
            title: '@:deliverable koppelen',
            success: '@:deliverable succesvol gekoppeld',
            error: '@:deliverable kon niet gekoppeld worden',
            form_error: 'Selecteer een @:deliverable',
        },
        en: {
            title: 'Link @.lower:deliverable',
            success: '@:deliverable successfully linked',
            error: 'Failed attempt to link @:deliverable',
            form_error: '@:deliverable missing',
        },
    },
});

const projectId = useRouteParams<string>('id');

const selectedSubProject = ref<string | undefined>(undefined);

const notificationStore = useNotificationStore();

const showDialog = defineModel<boolean>({ default: false });

const linkDeliverableInput = ref<Partial<LinkDeliverableInput>>({
    projectId: projectId.value,
    deliverableId: undefined,
});

const variables = computed(() => ({
    filters: [
        {
            field: 'id',
            value: projectId.value,
        },
    ],
}));

const { result: subProjectsResult, loading: isLoadingSubprojects } = useQuery(
    graphql(`
        query ProjectSubProjects($filters: [FilterCriterion]) {
            projects(filters: $filters) {
                items {
                    id
                    masterPortfolioId
                    subProjects {
                        name
                        description
                        id
                        finished
                        onHold
                        position
                    }
                }
            }
        }
    `),
    variables,
    {
        fetchPolicy: 'cache-first',
    }
);

const subProjects = computed<
    Pick<Project, 'name' | 'description' | 'id' | 'finished' | 'onHold'>[]
>(() => {
    if (isLoadingSubprojects.value) return [];
    return subProjectsResult.value?.projects.items[0]?.subProjects ?? [];
});

const filtersDeliverables = computed(() => ({
    filters: [
        {
            field: 'id',
            value: selectedSubProject.value,
        },
    ],
}));

const {
    load: loadDeliverables,
    refetch: refetchDeliverables,
    onResult: onDeliverablesResult,
} = useLazyQuery(
    graphql(`
        query ProjectDeliverables($filters: [FilterCriterion]) {
            projects(filters: $filters) {
                items {
                    id
                    name
                    deliverables {
                        id
                        name
                        linkedProjectId
                    }
                }
            }
        }
    `),
    filtersDeliverables.value
);

onDeliverablesResult((response) => {
    deliverables.value = response.data.projects.items[0].deliverables;
});

const deliverables = ref<
    Pick<Deliverable, 'name' | 'id' | 'linkedProjectId'>[]
>([]);

const deliverableFormInput = computed(() => {
    return deliverables.value
        .filter((d) => !d.linkedProjectId)
        .map((d) => ({
            name: d.name,
            id: d.id,
        }));
});

const { mutate: linkDeliverable } = useMutation<
    { linkDeliverable: { ok: boolean } },
    { input: LinkDeliverableInput }
>(
    graphql(`
        mutation LinkDeliverable($input: LinkDeliverableInput!) {
            linkDeliverable(input: $input) {
                ok
            }
        }
    `),
    {
        refetchQueries: ['ProjectPhases', 'ProjectDeliverables'],
        awaitRefetchQueries: true,
    }
);

function open() {
    showDialog.value = true;
}
defineExpose({ open });

function isLinkDeliverableInput(input: unknown): input is LinkDeliverableInput {
    if (!input) return false;
    if (typeof input !== 'object') return false;
    if ('projectId' in input && 'deliverableId' in input) return true;

    return false;
}

async function onSubmit(values: LinkDeliverableFormValues) {
    linkDeliverableInput.value.deliverableId = values.deliverableId;

    try {
        if (!isLinkDeliverableInput(linkDeliverableInput.value)) {
            notificationStore.add({
                type: 'error',
                message: t('form_error'),
            });
            return;
        }

        const response = await linkDeliverable({
            input: linkDeliverableInput.value,
        });

        if (response?.data?.linkDeliverable.ok) {
            showDialog.value = false;
            notificationStore.add({
                type: 'success',
                message: t('success'),
            });
        } else {
            notificationStore.add({
                type: 'error',
                message: t('error'),
            });
        }
    } catch {
        notificationStore.add({
            type: 'error',
            message: t('error'),
        });
    }
}

const subProjectFormInput = computed(() => {
    return subProjects.value.map((sp) => ({
        name: sp.name,
        id: sp.id,
    }));
});

watch(showDialog, async (newVal) => {
    if (newVal) {
        const response =
            (await loadDeliverables()) ||
            (await refetchDeliverables({
                filters: [{ field: 'id', value: selectedSubProject.value }],
            }));

        deliverables.value = response.data.projects.items[0].deliverables;
    }
});

watch(selectedSubProject, async (newVal) => {
    if (newVal) {
        const response =
            (await loadDeliverables()) ||
            (await refetchDeliverables({
                filters: [{ field: 'id', value: newVal }],
            }));

        deliverables.value = response.data.projects.items[0].deliverables;
    }
});

onMounted(() => {
    loadDeliverables();
});
</script>

<template>
    <AppModal
        v-model="showDialog"
        :title="t('title')"
        :width="600"
        height="auto"
    >
        <template #activator="{ props: activatorProps }">
            <slot
                v-if="subProjects.length > 0"
                name="activator"
                :props="activatorProps"
            />
        </template>

        <VCard>
            <VCardText>
                <DeliverableLinkForm
                    v-model="selectedSubProject"
                    :projects="subProjectFormInput"
                    :deliverables="deliverableFormInput"
                    @submit="onSubmit"
                    @cancel="showDialog = false"
                />
            </VCardText>
        </VCard>
    </AppModal>
</template>
